// Slide-9 multi-layer value tiles — revealed per persona post-registration.
import { motion } from "framer-motion";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, delay, ease: [0.16, 1, 0.3, 1] },
});

export const PERSONA_VALUE = {
  buyer: {
    label: "Importers", color: "text-blue-400", border: "border-blue-400/30", bg: "bg-blue-400/5",
    headline: "Pay Smart. Release Fast. Improve Cash Flow.",
    cycle: { traditional: "45-60 days", greenx: "20-30 days", saved: "20–30 days faster cash conversion" },
    bullets: [
      "Up to 30–45% improvement in working capital efficiency",
      "Faster document cycles & approvals",
      "Lower demurrage, storage & financing costs",
    ],
  },
  exporter: {
    label: "Exporters", color: "text-purple-400", border: "border-purple-400/30", bg: "bg-purple-400/5",
    headline: "Faster Receivables. Stronger Cash Flow.",
    cycle: { traditional: "60-75 days", greenx: "30-40 days", saved: "20–35 days faster cash conversion" },
    bullets: [
      "Up to 25–40% improvement in working capital efficiency",
      "Faster realization, more working capital",
      "Lower risk of delays & disputes",
    ],
  },
  downstream: {
    label: "Downstream Buyers", color: "text-cyan-400", border: "border-cyan-400/30", bg: "bg-cyan-400/5",
    headline: "Plan Better. Stock Right. Sell More.",
    metrics: [
      { value: "15–25%", label: "Reduction in Stockouts", desc: "Better availability, more sales" },
      { value: "10–20%", label: "Reduction in Excess Inventory", desc: "Lower holding & wastage" },
      { value: "5–10%", label: "Improvement in Forecast Accuracy", desc: "Smarter planning, lower risk" },
    ],
    bullets: ["Optimized inventory. Higher turns. Better margins."],
  },
  investor: {
    label: "Investors", color: "text-[#22C55E]", border: "border-[#22C55E]/30", bg: "bg-[#22C55E]/5",
    headline: "Lower Risk. Higher Visibility. Stronger Returns.",
    metrics: [
      { value: "2×", label: "Faster Cash Flow Cycles", desc: "Quicker capital rotation" },
      { value: "15–25%", label: "Higher Capital Efficiency", desc: "More output per dollar invested" },
      { value: "30–40%", label: "Lower Risk of Delay & Default", desc: "Data-driven risk intelligence" },
    ],
    bullets: ["Better visibility — real-time portfolio & trade performance"],
  },
};

export function MultiLayerValue({ persona = "buyer" }) {
  const v = PERSONA_VALUE[persona];
  if (!v) return null;
  return (
    <section className="max-w-[1200px] mx-auto px-6 pb-12" data-testid={`multilayer-${persona}`}>
      <motion.div {...fade(0)} className="flex items-center gap-2 mb-3">
        <span className={`text-[10px] font-semibold uppercase tracking-[0.3em] ${v.color}`}>
          Your value layer · {v.label}
        </span>
      </motion.div>
      <motion.h2 {...fade(0.05)} className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-white mb-8 leading-tight max-w-3xl">
        {v.headline}
      </motion.h2>

      {/* Cash-cycle comparison for buyer/exporter */}
      {v.cycle && (
        <motion.div {...fade(0.1)} className={`grid grid-cols-1 md:grid-cols-3 gap-3 mb-6`}>
          <div className="border border-red-400/30 bg-red-400/5 p-5">
            <p className="text-red-400 text-[10px] uppercase tracking-wider mb-1">Traditional</p>
            <p className="font-['Outfit'] text-3xl font-bold text-red-400">{v.cycle.traditional}</p>
          </div>
          <div className={`border ${v.border} ${v.bg} p-5`}>
            <p className={`${v.color} text-[10px] uppercase tracking-wider mb-1`}>With Delta GreenX</p>
            <p className={`font-['Outfit'] text-3xl font-bold ${v.color}`}>{v.cycle.greenx}</p>
          </div>
          <div className="border border-[#22C55E]/30 bg-[#22C55E]/5 p-5 flex flex-col justify-center">
            <p className="text-[#22C55E] text-[10px] uppercase tracking-wider mb-1">Outcome</p>
            <p className="font-['Outfit'] text-xl font-bold text-[#22C55E] leading-tight">{v.cycle.saved}</p>
          </div>
        </motion.div>
      )}

      {/* Stat metrics for downstream/investor */}
      {v.metrics && (
        <motion.div {...fade(0.1)} className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
          {v.metrics.map((m, i) => (
            <div key={i} className={`border ${v.border} ${v.bg} p-5`}>
              <p className={`font-['Outfit'] text-3xl font-bold ${v.color}`}>{m.value}</p>
              <p className="text-white text-sm font-semibold mt-1">{m.label}</p>
              <p className="text-[#A1B2A8] text-xs mt-1">{m.desc}</p>
            </div>
          ))}
        </motion.div>
      )}

      <motion.ul {...fade(0.2)} className="space-y-2">
        {v.bullets.map((b, i) => (
          <li key={i} className="text-[#C5D5CC] text-sm flex items-start gap-2">
            <span className={`${v.color} mt-0.5`}>▸</span>
            <span>{b}</span>
          </li>
        ))}
      </motion.ul>
    </section>
  );
}
